(ns portal.user-ocp.db
  (:require [clojure.set :refer [rename-keys]]
            [clojure.spec.alpha :as spec]
            [clojure.string :as str]
            [common.form.core :as form]
            [common.validators.core :refer [valid-email?]]
            [phrase.alpha :as p]
            [re-frame.core :as rf]))

(def entity-name "User")

(spec/def ::email
  (spec/and (complement str/blank?)
            valid-email?))

(spec/def ::roleId some?)

(spec/def ::value
  (spec/keys :req-un [::email ::roleId]))

(p/defphraser (complement str/blank?)
  [_ problem]
  {:message "The field should not be empty"
   :in (:in problem)
   :problem problem})

(p/defphraser string?
  [_ problem key]
  {:message "The field should be a string"
   :in (:in problem)
   :problem problem})

(p/defphraser valid-email?
  [_ problem]
  {:message "Not a valid email address"
   :in (:in problem)
   :problem problem})

(p/defphraser some?
  [_ problem]
  {:message "The field should not be empty"
   :in (:in problem)
   :problem problem})

(def initial-value
  {:email ""
   :roleId nil})

(def validate-form
  (rf/enrich
   (form/get-validate-form-fn :form.user ::value)))

(def ui-messages {:create {:success "Successfully created user"
                           :failure "Failed to create user"}
                  :edit {:success "Successfully saved user"
                         :failure "Failed to save user"}
                  :delete {:success "Successfully removed access for user"
                           :failure "Failed to remove access for user"}})

(def forward-events
  [{:events #{:resource.user/post-success
              :resource.user/patch-me-success
              :resource.user/change-role-success}
    :dispatch-to [:form.user/submit-success]}
   {:events #{:resource.user/post-failure
              :resource.user/patch-me-failure
              :resource.user/change-role-failure}
    :dispatch-to [:form.user/submit-failure]}
   {:events #{:resource.user/delete-success}
    :dispatch-to [:form.user/delete-success]}
   {:events #{:resource.user/delete-failure}
    :dispatch-to [:form.user/delete-failure]}])

(form/register-form {:namespace :form.user
                     :initial-value initial-value
                     :validate-form validate-form
                     :forward-events forward-events
                     :submit-event :form.user/submit-form})

(rf/reg-event-fx :form.user/init-form
  (fn [{:keys [db]} [_ user]]
    (let [dispatch [[:form.user/init]
                    [:resource.roles/get-all-roles]]
          user (dissoc user :name)]
      (cond-> {:db db}
        user (update-in [:db :form.user] assoc :value user :initial-value user :mode :edit)
        (not user) (assoc-in [:db :form.user :mode] :create)
        true (assoc :dispatch-n (remove nil? dispatch))))))

(rf/reg-event-fx :form.user/submit-form
  [(rf/inject-cofx :sub :form.user/mode)]
  (fn [cofx [_ payload]]
    (let [event (if (= :edit (:form.user/mode cofx))
                  :resource.user/change-role
                  :resource.user/post)]
      {:dispatch-n [[:loader/open]
                    [event payload]]})))

(rf/reg-event-fx :form.user/submit-success
  [(rf/inject-cofx :sub :form.user/mode)]
  (fn [{:form.user/keys [mode]} _]
    {:fx [[:dispatch [:ocp/close]]
          [:dispatch [:loader/close]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (get-in ui-messages [mode :success])}]]
          [:invalidate-queries {:queryKey ["users"]}]]}))

(defn get-field-errors
  [mapping error]
  (when-let [error-code-by-field (->> (first (get-in error [:error :spec]))
                                      (map (fn [[k v]]
                                             [k (first v)]))
                                      (into {}))]
    (->> error-code-by-field
         (keep (fn [[field error-code]]
                 (when-let [error-message (get-in mapping [field error-code])]
                   [field error-message])))
         (into {}))))

(def error-mapping
  {:email {"duplicate" "This email address is already in use"}})

(rf/reg-event-fx :form.user/submit-failure
  (fn [{:keys [db]} [event [_ response]]]
    (let [error-messages (get-field-errors error-mapping (:value response))
          errors (->> error-messages
                      (map (fn [[k v]]
                             [k [{:message v}]]))
                      (into {}))
          mode (get-in db [:form.user :mode])]
      {:db (update-in db [:form.user] assoc :errors errors)
       :dispatch-n [[:loader/close]
                    [:response-handler/handle-response
                     {:entity-name entity-name
                      :type :error
                      :event event
                      :message (get-in ui-messages [mode :failure])
                      :response response}]]})))

(rf/reg-event-fx :form.user/delete
  (fn [{:keys [db]} _]
    {:dispatch-n [[:loader/open]
                  [:resource.user/delete (get-in db [:form.user :value :id])]]}))

(rf/reg-event-fx :form.user/delete-success
  (fn [_]
    {:fx [[:dispatch [:ocp/close]]
          [:dispatch [:loader/close]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (get-in ui-messages [:delete :success])}]]
          [:invalidate-queries {:queryKey ["users"]}]]}))

(rf/reg-event-fx :form.user/delete-failure
  (fn [_ [event response]]
    {:dispatch-n [[:loader/close]
                  [:response-handler/handle-response
                   {:entity-name entity-name
                    :type :error
                    :event event
                    :message (get-in ui-messages [:delete :failure])
                    :response response}]]}))

(rf/reg-sub :form.user/role-options
  :<- [:resource.roles/all]
  (fn [roles]
    (->> roles
         :value
         (map #(rename-keys % {:name :label
                               :id :value})))))

(rf/reg-sub :form.user/mode
  (fn [db]
    (get-in db [:form.user :mode])))

(def use-bind-control (partial form/use-bind-control :form.user))
(def use-error-message (partial form/use-error-message :form.user))
